import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { io } from 'socket.io-client';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import axios from '../../api/axios';

const RequestsManager = ({ onRequestViewed }) => {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    fetchRequests();
    fetchFormFields();

    // Подключаемся к WebSocket для обновления списка в реальном времени
    let socketUrl;
    const apiUrl = process.env.REACT_APP_API_URL;
    
    if (!apiUrl || apiUrl === '/api' || apiUrl.startsWith('/api')) {
      // Относительный URL или не установлен - используем текущий origin
      socketUrl = window.location.origin;
    } else if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
      socketUrl = apiUrl.replace('/api', '');
    } else {
      // Fallback на текущий origin
      socketUrl = window.location.origin;
    }
    
    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    // Обновляем список при получении новых заявок
    socketRef.current.on('new-request', () => {
      fetchRequests();
    });

    socketRef.current.on('request-updated', () => {
      fetchRequests();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get('/requests');
      setRequests(response.data);
    } catch (error) {
      console.error('Ошибка загрузки заявок:', error);
    }
  };

  const fetchFormFields = async () => {
    try {
      const response = await axios.get('/form-fields');
      setFormFields(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Ошибка загрузки полей формы:', error);
    }
  };

  const getFieldLabel = (name) => {
    const field = formFields.find((f) => f.name === name);
    return field?.label || name;
  };

  const getCustomFieldEntries = (request) => {
    const customFields = request?.customFields || {};
    const entries = Object.entries(customFields).filter(([, value]) => value !== '' && value != null);

    // Сортируем по порядку полей из конструктора форм, неизвестные - в конец
    return entries.sort((a, b) => {
      const orderA = formFields.find((f) => f.name === a[0])?.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = formFields.find((f) => f.name === b[0])?.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  };

  const handleView = async (request) => {
    setSelectedRequest(request);
    setOpen(true);
    // Помечаем заявку как просмотренную
    if (!request.isViewed) {
      try {
        await axios.put(`/requests/${request._id}/viewed`);
        fetchRequests();
        // Обновляем счетчик в родительском компоненте
        if (onRequestViewed) {
          onRequestViewed();
        }
      } catch (error) {
        console.error('Ошибка пометки заявки как просмотренной:', error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRequest(null);
    // Обновляем счетчик после закрытия диалога
    if (onRequestViewed) {
      setTimeout(() => onRequestViewed(), 500);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту заявку?')) {
      try {
        await axios.delete(`/requests/${id}`);
        fetchRequests();
        // Обновляем счетчик после удаления
        if (onRequestViewed) {
          setTimeout(() => onRequestViewed(), 500);
        }
      } catch (error) {
        console.error('Ошибка удаления заявки:', error);
      }
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`/requests/${id}`, { status });
      fetchRequests();
      setOpen(false);
      // Обновляем счетчик после изменения статуса
      if (onRequestViewed) {
        setTimeout(() => onRequestViewed(), 500);
      }
    } catch (error) {
      console.error('Ошибка изменения статуса:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'info',
      processing: 'warning',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'Новая',
      processing: 'В обработке',
      completed: 'Завершена',
      cancelled: 'Отменена',
    };
    return labels[status] || status;
  };

  const unviewedCount = requests.filter((r) => !r.isViewed).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Управление заявками
        </Typography>
        {unviewedCount > 0 && (
          <Chip
            label={`Непросмотренных: ${unviewedCount}`}
            color="error"
            size="small"
          />
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Имя</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Телефон</TableCell>
              <TableCell>Дата</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow
                key={request._id}
                sx={{
                  backgroundColor: !request.isViewed ? 'rgba(211, 47, 47, 0.05)' : 'inherit',
                  '&:hover': {
                    backgroundColor: !request.isViewed ? 'rgba(211, 47, 47, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!request.isViewed && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'error.main',
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: !request.isViewed ? 600 : 400 }}
                    >
                      {request.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{request.email}</TableCell>
                <TableCell>{request.phone}</TableCell>
                <TableCell>
                  {format(new Date(request.createdAt), 'dd.MM.yyyy HH:mm')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(request.status)}
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleView(request)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(request._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Детали заявки</DialogTitle>
        {selectedRequest && (
          <>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Имя
                </Typography>
                <Typography variant="body1">{selectedRequest.name}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{selectedRequest.email}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Телефон
                </Typography>
                <Typography variant="body1">{selectedRequest.phone}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Сообщение
                </Typography>
                <Typography variant="body1">
                  {selectedRequest.message || 'Не указано'}
                </Typography>
              </Box>
              {getCustomFieldEntries(selectedRequest).map(([name, value]) => (
                <Box sx={{ mb: 2 }} key={name}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {getFieldLabel(name)}
                  </Typography>
                  <Typography variant="body1">{String(value)}</Typography>
                </Box>
              ))}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Статус
                </Typography>
                <Chip
                  label={getStatusLabel(selectedRequest.status)}
                  color={getStatusColor(selectedRequest.status)}
                  size="small"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Дата создания
                </Typography>
                <Typography variant="body1">
                  {format(new Date(selectedRequest.createdAt), 'dd.MM.yyyy HH:mm')}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Закрыть</Button>
              {selectedRequest.status !== 'completed' && (
                <Button
                  onClick={() =>
                    handleStatusChange(selectedRequest._id, 'completed')
                  }
                  variant="contained"
                  color="success"
                >
                  Завершить
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default RequestsManager;
